import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
    CAlert,
    CBadge,
    CButton,
    CCard,
    CCardBody,
    CCardHeader,
    CFormInput,
    CSpinner,
    CTable,
    CTableBody,
    CTableDataCell,
    CTableHead,
    CTableHeaderCell,
    CTableRow,
} from "@coreui/react";

import { deleteExpense, fetchExpenses, updateExpense } from "../api/expenses";
import { t } from "../i18n";

export default function ExpensesPage() {
    const [expenses, setExpenses] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editingExpenseId, setEditingExpenseId] = useState(null);
    const [editTitle, setEditTitle] = useState("");
    const [editMatchers, setEditMatchers] = useState([]);
    const [savingExpenseId, setSavingExpenseId] = useState(null);
    const [deletingExpenseId, setDeletingExpenseId] = useState(null);

    useEffect(() => {
        let isMounted = true;

        async function loadExpenses() {
            try {
                const loadedExpenses = await fetchExpenses();

                if (isMounted) {
                    setExpenses(loadedExpenses);
                    setError(null);
                }
            } catch (loadError) {
                if (isMounted) {
                    setError(loadError.message);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        }

        loadExpenses();

        return () => {
            isMounted = false;
        };
    }, []);

    function startEditing(expense) {
        setError(null);
        setEditingExpenseId(expense.id);
        setEditTitle(expense.title);
        setEditMatchers(expense.matchers.map((matcher) => ({ ...matcher })));
    }

    function cancelEditing() {
        setEditingExpenseId(null);
        setEditTitle("");
        setEditMatchers([]);
    }

    function updateMatcher(index, title) {
        setEditMatchers((currentMatchers) =>
            currentMatchers.map((matcher, matcherIndex) =>
                matcherIndex === index ? { ...matcher, title } : matcher
            )
        );
    }

    function addMatcher() {
        setEditMatchers((currentMatchers) => [
            ...currentMatchers,
            { title: "" },
        ]);
    }

    function removeMatcher(index) {
        setEditMatchers((currentMatchers) => {
            const matcher = currentMatchers[index];

            if (matcher.id === undefined) {
                return currentMatchers.filter((_, matcherIndex) => matcherIndex !== index);
            }

            return currentMatchers.map((currentMatcher, matcherIndex) =>
                matcherIndex === index
                    ? { ...currentMatcher, destroy: true }
                    : currentMatcher
            );
        });
    }

    function changedMatcherAttributes(expense) {
        const originalMatchersById = new Map(
            expense.matchers.map((matcher) => [matcher.id, matcher])
        );

        return editMatchers
            .map((matcher) => ({
                id: matcher.id,
                title: matcher.title.trim(),
                destroy: matcher.destroy,
            }))
            .filter((matcher) => {
                if (matcher.destroy === true) {
                    return matcher.id !== undefined;
                }

                if (matcher.id === undefined) {
                    return matcher.title.length > 0;
                }

                const originalMatcher = originalMatchersById.get(matcher.id);

                return originalMatcher && matcher.title !== originalMatcher.title;
            });
    }

    async function saveExpense(expenseId) {
        setError(null);
        setSavingExpenseId(expenseId);

        const originalExpense = expenses.find((expense) => expense.id === expenseId);
        const changedMatchers = changedMatcherAttributes(originalExpense);

        const trimmedTitle = editTitle.trim();
        const titleChanged = trimmedTitle !== originalExpense.title;

        if (!titleChanged && changedMatchers.length === 0) {
            cancelEditing();
            setSavingExpenseId(null);
            return;
        }

        try {
            const updatedExpense = await updateExpense({
                id: expenseId,
                title: titleChanged ? trimmedTitle : undefined,
                matchers: changedMatchers.length > 0 ? changedMatchers : undefined,
            });

            setExpenses((currentExpenses) =>
                currentExpenses.map((expense) =>
                    expense.id === updatedExpense.id ? updatedExpense : expense
                ).sort(compareExpensesByTitle)
            );
            cancelEditing();
        } catch (saveError) {
            setError(saveError.message);
        } finally {
            setSavingExpenseId(null);
        }
    }

    async function destroyExpense(expenseId) {
        if (!window.confirm(t("expenses.confirmDelete"))) {
            return;
        }

        setError(null);
        setDeletingExpenseId(expenseId);

        try {
            await deleteExpense(expenseId);

            setExpenses((currentExpenses) =>
                currentExpenses.filter((expense) => expense.id !== expenseId)
            );

            if (editingExpenseId === expenseId) {
                cancelEditing();
            }
        } catch (destroyError) {
            setError(destroyError.message);
        } finally {
            setDeletingExpenseId(null);
        }
    }

    return (
        <>
            <div className="d-flex justify-content-between align-items-start gap-3 mb-4">
                <div>
                    <h1>{t("expenses.title")}</h1>
                    <p className="text-body-secondary mb-0">
                        {t("expenses.description")}
                    </p>
                </div>

                <CButton color="primary" as={Link} to="/expenses/new">
                    {t("expenses.create")}
                </CButton>
            </div>

            <CCard>
                <CCardHeader>
                    <strong>{t("expenses.list")}</strong>
                </CCardHeader>

                <CCardBody>
                    {error && (
                        <CAlert color="danger" className={expenses.length > 0 ? "mb-3" : "mb-0"}>
                            {error}
                        </CAlert>
                    )}

                    {!error && loading && (
                        <div className="d-flex align-items-center gap-2 text-body-secondary">
                            <CSpinner size="sm" />
                            {t("expenses.loading")}
                        </div>
                    )}

                    {!error && !loading && expenses.length === 0 && (
                        <div className="text-center py-5">
                            <h5>{t("expenses.emptyTitle")}</h5>
                            <p className="text-body-secondary">
                                {t("expenses.emptyDescription")}
                            </p>

                            <CButton color="primary" as={Link} to="/expenses/new">
                                {t("expenses.create")}
                            </CButton>
                        </div>
                    )}

                    {!loading && expenses.length > 0 && (
                        <CTable responsive hover className="mb-0 align-middle">
                            <CTableHead>
                                <CTableRow>
                                    <CTableHeaderCell scope="col">{t("expenses.table.title")}</CTableHeaderCell>
                                    <CTableHeaderCell scope="col">{t("expenses.table.matchers")}</CTableHeaderCell>
                                    <CTableHeaderCell scope="col" className="text-end">
                                        {t("expenses.table.actions")}
                                    </CTableHeaderCell>
                                </CTableRow>
                            </CTableHead>

                            <CTableBody>
                                {expenses.map((expense) => {
                                    const isEditing = editingExpenseId === expense.id;
                                    const isSaving = savingExpenseId === expense.id;
                                    const isDeleting = deletingExpenseId === expense.id;
                                    const titleChanged = editTitle.trim() !== expense.title;
                                    const matchersChanged =
                                        isEditing && changedMatcherAttributes(expense).length > 0;
                                    const canSave =
                                        editTitle.trim().length > 0 &&
                                        !isSaving &&
                                        (titleChanged || matchersChanged);

                                    return (
                                        <CTableRow key={expense.id}>
                                            <CTableDataCell className="fw-semibold">
                                                {isEditing ? (
                                                    <CFormInput
                                                        aria-label={t("expenses.titleAria")}
                                                        value={editTitle}
                                                        onChange={(event) =>
                                                            setEditTitle(event.target.value)
                                                        }
                                                        required
                                                    />
                                                ) : (
                                                    expense.title
                                                )}
                                            </CTableDataCell>

                                            <CTableDataCell>
                                                {isEditing ? (
                                                    <div className="d-grid gap-2">
                                                        {editMatchers.map((matcher, index) => (
                                                            matcher.destroy ? null : (
                                                                <div className="d-flex gap-2" key={matcher.id || `new-${index}`}>
                                                                    <CFormInput
                                                                        aria-label={t("expenses.matcherAria")}
                                                                        placeholder={t("expenses.matcherPlaceholder")}
                                                                        value={matcher.title}
                                                                        onChange={(event) =>
                                                                            updateMatcher(index, event.target.value)
                                                                        }
                                                                    />

                                                                    <CButton
                                                                        color="danger"
                                                                        variant="outline"
                                                                        type="button"
                                                                        onClick={() => removeMatcher(index)}
                                                                    >
                                                                        {t("expenses.removeMatcher")}
                                                                    </CButton>
                                                                </div>
                                                            )
                                                        ))}

                                                        {editMatchers.filter((matcher) => !matcher.destroy).length === 0 && (
                                                            <span className="text-body-secondary">
                                                                {t("expenses.noMatchers")}
                                                            </span>
                                                        )}

                                                        <div>
                                                            <CButton
                                                                color="secondary"
                                                                variant="outline"
                                                                type="button"
                                                                onClick={addMatcher}
                                                            >
                                                                {t("expenses.addMatcher")}
                                                            </CButton>
                                                        </div>
                                                    </div>
                                                ) : expense.hasMatchers ? (
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {expense.matcherTitles.map((title, index) => (
                                                            <CBadge color="secondary" key={`${title}-${index}`}>
                                                                {title}
                                                            </CBadge>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <span className="text-body-secondary">
                                                        {t("expenses.noMatchers")}
                                                    </span>
                                                )}
                                            </CTableDataCell>

                                            <CTableDataCell className="text-end">
                                                {isEditing ? (
                                                    <div className="d-flex justify-content-end gap-2">
                                                        <CButton
                                                            color="primary"
                                                            type="button"
                                                            disabled={!canSave}
                                                            onClick={() => saveExpense(expense.id)}
                                                        >
                                                            {isSaving ? t("expenses.saving") : t("expenses.save")}
                                                        </CButton>

                                                        <CButton
                                                            color="secondary"
                                                            variant="outline"
                                                            type="button"
                                                            disabled={isSaving}
                                                            onClick={cancelEditing}
                                                        >
                                                            {t("expenses.cancel")}
                                                        </CButton>
                                                    </div>
                                                ) : (
                                                    <div className="d-flex justify-content-end gap-2">
                                                        <CButton
                                                            color="secondary"
                                                            variant="outline"
                                                            type="button"
                                                            disabled={isDeleting}
                                                            onClick={() => startEditing(expense)}
                                                        >
                                                            {t("expenses.edit")}
                                                        </CButton>

                                                        <CButton
                                                            color="danger"
                                                            variant="outline"
                                                            type="button"
                                                            disabled={isDeleting}
                                                            onClick={() => destroyExpense(expense.id)}
                                                        >
                                                            {isDeleting ? t("expenses.deleting") : t("expenses.delete")}
                                                        </CButton>
                                                    </div>
                                                )}
                                            </CTableDataCell>
                                        </CTableRow>
                                    );
                                })}
                            </CTableBody>
                        </CTable>
                    )}
                </CCardBody>
            </CCard>
        </>
    );
}

function compareExpensesByTitle(left, right) {
    return left.title.localeCompare(right.title, "lt", { sensitivity: "base" });
}
